import { Upload } from 'tus-js-client';
import type { UploadPlan } from '@daily/contracts/api';

export interface UploadTransport {
  send(plan: UploadPlan, file: File, progress: (fraction: number) => void, signal: AbortSignal): Promise<void>;
}
export const directUploadTransport: UploadTransport = {
  send(plan, file, progress, signal) {
    if (!plan.url || plan.transport === 'none') return Promise.reject(new Error('A usable upload plan is unavailable.'));
    if (signal.aborted) return Promise.reject(new Error('Upload cancelled.'));
    const uploadUrl = plan.url;
    if (plan.transport === 'tus') return new Promise((resolve, reject) => {
      // The SDK owns this protocol header. XHR appends duplicates instead of replacing them.
      const headers = Object.fromEntries(Object.entries(plan.headers).filter(([name]) => name.toLowerCase() !== 'tus-resumable'));
      const transfer = new Upload(file, { uploadUrl, headers,
        onBeforeRequest: (request) => { const underlying: unknown = request.getUnderlyingObject(); if (underlying instanceof XMLHttpRequest) underlying.withCredentials = false; },
        retryDelays: [0, 1000, 3000], chunkSize: 5 * 1024 * 1024, storeFingerprintForResuming: false,
        onProgress: (sent, total) => progress(total > 0 ? sent / total : 0),
        onError: () => { cleanup(); reject(new Error('Resumable video transfer failed. Retry with the same file.')); },
        onSuccess: () => { cleanup(); resolve(); },
      });
      const abort = () => { void transfer.abort().finally(() => { cleanup(); reject(new Error('Upload cancelled.')); }); };
      const cleanup = () => signal.removeEventListener('abort', abort);
      signal.addEventListener('abort', abort, { once: true }); transfer.start();
    });
    return new Promise((resolve, reject) => {
      const request = new XMLHttpRequest();
      const abort = () => request.abort();
      const cleanup = () => signal.removeEventListener('abort', abort);
      request.open('PUT', plan.url ?? ''); request.withCredentials = false;
      for (const [name, value] of Object.entries(plan.headers)) request.setRequestHeader(name, value);
      request.upload.onprogress = (event) => { if (event.lengthComputable) progress(event.loaded / event.total); };
      request.onload = () => { cleanup(); if (request.status >= 200 && request.status < 300) resolve(); else reject(new Error(`Direct media transfer failed (${request.status}).`)); };
      request.onerror = () => { cleanup(); reject(new Error('Direct media transfer failed. Check your connection and retry.')); };
      request.onabort = () => { cleanup(); reject(new Error('Upload cancelled.')); };
      signal.addEventListener('abort', abort, { once: true }); request.send(file);
    });
  },
};
